import React, { useState, createElement } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { StatusTimeline } from '../components/tracking/StatusTimeline';
import { useOrderTracking } from '../lib/hooks';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { formatCurrency, getStatusColor, getStatusLabel } from '../lib/utils';
import { Search, MapPin, Store, Receipt, Phone, Copy, CheckCircle2 } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import { useLanguage } from '../lib/i18n';
export function TrackingPage() {
  const {
    trackingToken
  } = useParams<{
    trackingToken: string;
  }>();
  const {
    order,
    events,
    loading,
    error
  } = useOrderTracking(trackingToken);
  const [copied, setCopied] = useState(false);
  const {
    t
  } = useLanguage();
  const handleCopyLink = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(window.location.href);
      } else {
        // Safari / WhatsApp fallback using execCommand
        const textArea = document.createElement('textarea');
        textArea.value = window.location.href;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silently fail if clipboard is blocked
      setCopied(false);
    }
  };
  if (loading) {
    return <div className="min-h-screen flex flex-col bg-brand-offwhite" data-id="element-4864">
        <MarketplaceHeader data-id="element-4865" />
        <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-12" data-id="element-4866">
          <Skeleton className="h-12 w-1/2 mb-8" data-id="element-4867" />
          <div className="bg-white rounded-2xl p-6 shadow-sm mb-8" data-id="element-4868">
            <Skeleton className="h-8 w-1/3 mb-4" data-id="element-4869" />
            <Skeleton className="h-4 w-full mb-2" data-id="element-4870" />
            <Skeleton className="h-4 w-2/3" data-id="element-4871" />
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-4872">
            {[1, 2, 3, 4].map(i => <div key={i} className="flex gap-4 mb-6" data-id="element-4873">
                <Skeleton className="h-10 w-10 rounded-full shrink-0" data-id="element-4874" />
                <div className="flex-1" data-id="element-4875">
                  <Skeleton className="h-6 w-1/3 mb-2" data-id="element-4876" />
                  <Skeleton className="h-4 w-full" data-id="element-4877" />
                </div>
              </div>)}
          </div>
        </main>
        <Footer data-id="element-4878" />
      </div>;
  }
  if (error || !order) {
    return <div className="min-h-screen flex flex-col bg-brand-offwhite" data-id="element-4879">
        <MarketplaceHeader data-id="element-4880" />
        <main className="flex-1 flex items-center justify-center py-12" data-id="element-4881">
          <EmptyState icon={Search} title={t('tracking_not_found')} description={t('error')} actionLabel={t('confirm_back')} onAction={() => window.location.href = '/'} data-id="element-4882" />
        </main>
        <Footer data-id="element-4883" />
      </div>;
  }
  const trackingUrl = window.location.href;
  const statusLabel = t(`status_${order.status}`) || getStatusLabel(order.status);
  return <div className="min-h-screen flex flex-col bg-brand-offwhite" data-id="element-4884">
      <MarketplaceHeader data-id="element-4885" />

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-8 sm:py-12" data-id="element-4886">
        <div className="flex items-center justify-between mb-8" data-id="element-4887">
          <h1 className="text-3xl font-bold text-gray-900" data-id="element-4888">
            {t('tracking_title')}
          </h1>
          <Badge variant="secondary" className={`text-sm px-3 py-1 ${getStatusColor(order.status)}`} data-id="element-4889">
            {statusLabel}
          </Badge>
        </div>

        {/* Order Info Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-8" data-id="element-4890">
          <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-100" data-id="element-4891">
            <div data-id="element-4892">
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-4893">
                {t('confirm_order_number')}
              </p>
              <p className="text-2xl font-black text-gray-900" data-id="element-4894">
                #{order.order_number}
              </p>
            </div>
            <div className="text-right" data-id="element-4895">
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-4896">
                {t('total')}
              </p>
              <p className="text-2xl font-black text-brand-primary" data-id="element-4897">
                {formatCurrency(order.total)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4898">
            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-start gap-4" data-id="element-4899">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0" data-id="element-4900">
                <Store className="w-5 h-5 text-blue-600" data-id="element-4901" />
              </div>
              <div data-id="element-4902">
                <h3 className="text-sm font-semibold text-gray-500 mb-1" data-id="element-4903">
                  Restaurante
                </h3>
                <p className="font-medium text-gray-900" data-id="element-4904">
                  Pedido del restaurante
                </p>
                <p className="text-sm text-gray-500 mt-1" data-id="element-4905">
                  {order.delivery_type === 'pickup' ? 'Para llevar' : order.delivery_type === 'delivery' ? 'Delivery' : 'Comer aquí'}
                </p>
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-start gap-4" data-id="element-4906">
              <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center shrink-0" data-id="element-4907">
                <Receipt className="w-5 h-5 text-emerald-600" data-id="element-4908" />
              </div>
              <div data-id="element-4909">
                <h3 className="text-sm font-semibold text-gray-500 mb-1" data-id="element-4910">
                  {t('total')}
                </h3>
                <p className="font-bold text-xl text-gray-900" data-id="element-4911">
                  {formatCurrency(order.total)}
                </p>
                <p className="text-sm text-gray-500 mt-1" data-id="element-4912">
                  Pago:{' '}
                  {order.payment_method === 'sinpe' ? 'SINPE Móvil' : order.payment_method === 'cash' ? 'Efectivo' : 'Tarjeta'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Timeline */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8 mb-8" data-id="element-4913">
          <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-4914">
            {t('tracking_title')}
          </h2>
          <StatusTimeline currentStatus={order.status} events={events} restaurantId={order.restaurant_id} data-id="element-4915" />
        </div>

        {/* Delivery Info if applicable */}
        {order.delivery_type === 'delivery' && order.delivery_address && <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-8" data-id="element-4916">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-4917">
              <MapPin className="w-5 h-5 text-gray-400" data-id="element-4918" />
              {t('checkout_address')}
            </h3>
            <p className="text-gray-600" data-id="element-4919">{order.delivery_address}</p>
            {order.notes && <div className="mt-4 p-3 bg-amber-50 rounded-xl text-sm text-amber-800 border border-amber-100" data-id="element-4920">
                <span className="font-semibold" data-id="element-4921">Notas:</span> {order.notes}
              </div>}
          </div>}

        {/* Tracking Link */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-4922">
          <p className="text-sm font-medium text-gray-900 mb-4" data-id="element-4923">
            {t('copy_tracking')}
          </p>
          <div className="flex items-center gap-3" data-id="element-4924">
            <div className="flex-1 bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-500 truncate border border-gray-100" data-id="element-4925">
              {trackingUrl}
            </div>
            <button onClick={handleCopyLink} className="shrink-0 flex items-center justify-center w-12 h-12 rounded-xl border-2 border-gray-200 hover:border-brand-primary hover:text-brand-primary transition-colors" data-id="element-4926">
              {copied ? <CheckCircle2 className="w-5 h-5 text-green-600" data-id="element-4927" /> : <Copy className="w-5 h-5" data-id="element-4928" />}
            </button>
          </div>
        </div>
      </main>

      <Footer data-id="element-4929" />
    </div>;
}